import { Component } from '@theme/component';
import { ThemeEvents, CartErrorEvent, CartAddEvent } from '@theme/events';

/**
 * @typedef {Object} GiftCardRecipientFormRefs
 * @property {HTMLInputElement} myEmailButton - Button for selecting my email option
 * @property {HTMLInputElement} recipientEmailButton - Button for selecting recipient email option
 * @property {HTMLDivElement} recipientFields - Container for recipient form fields
 * @property {HTMLInputElement} recipientEmail - Recipient email input field
 * @property {HTMLInputElement} recipientName - Recipient name input field
 * @property {HTMLTextAreaElement} recipientMessage - Recipient message textarea
 * @property {HTMLInputElement} recipientSendOn - Send on date input
 * @property {HTMLInputElement} [timezoneOffset] - Timezone offset hidden input (optional)
 * @property {HTMLInputElement} [controlFlag] - Shopify gift card control flag (optional as it's dynamically queried)
 * @property {HTMLDivElement} [emailError] - Email error message container (optional)
 * @property {HTMLDivElement} [nameError] - Name error message container (optional)
 * @property {HTMLDivElement} [messageError] - Message error message container (optional)
 * @property {HTMLDivElement} [sendOnError] - Send on error message container (optional)
 * @property {HTMLSpanElement} [characterCount] - Character count display element (optional)
 * @property {HTMLDivElement} [liveRegion] - Live region for screen reader announcements (optional)
 */

/**
 * @extends {Component<GiftCardRecipientFormRefs>}
 */
class GiftCardRecipientForm extends Component {
  static DeliveryMode = {
    SELF: 'self', // Send to my email
    RECIPIENT: 'recipient_form', // Send to recipient's email with form
  };

  #currentMode = GiftCardRecipientForm.DeliveryMode.SELF;

  // Store bound event handlers for cleanup
  /** @type {(() => void) | null} */
  #updateCharacterCountBound = null;
  /** @type {((event: Event) => void) | null} */
  #displayCartErrorBound = null;
  /** @type {((event: Event) => void) | null} */
  #cartAddEventBound = null;

  /** Set once the shopper has tried to add with recipient details: from then on every field re-checks as it's edited. */
  #showAllErrors = false;

  requiredRefs = [
    'myEmailButton',
    'recipientEmailButton',
    'recipientFields',
    'recipientEmail',
    'recipientName',
    'recipientMessage',
    'recipientSendOn',
  ];

  /**
   * Get all recipient input fields
   * @returns {(HTMLInputElement | HTMLTextAreaElement)[]} Array of input fields
   */
  get #inputFields() {
    return [this.refs.recipientEmail, this.refs.recipientName, this.refs.recipientMessage, this.refs.recipientSendOn];
  }

  connectedCallback() {
    super.connectedCallback();
    this.#initializeForm();

    this.#updateCharacterCountBound = () => this.#updateCharacterCount();
    this.refs.recipientMessage.addEventListener('input', this.#updateCharacterCountBound);

    this.#displayCartErrorBound = this.#displayCartError.bind(this);
    // @ts-ignore - #displayCartErrorBound is guaranteed to be non-null here
    document.addEventListener(ThemeEvents.cartError, this.#displayCartErrorBound);

    this.#cartAddEventBound = (event) => this.#handleCartAdd(event);
    document.addEventListener(ThemeEvents.cartUpdate, this.#cartAddEventBound);

    this.addEventListener('input', this.#onFieldInput);
    this.addEventListener('focusout', this.#onFieldBlur);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#updateCharacterCountBound) {
      this.refs.recipientMessage.removeEventListener('input', this.#updateCharacterCountBound);
      this.#updateCharacterCountBound = null;
    }

    if (this.#displayCartErrorBound) {
      document.removeEventListener(ThemeEvents.cartError, this.#displayCartErrorBound);
      this.#displayCartErrorBound = null;
    }

    if (this.#cartAddEventBound) {
      document.removeEventListener(ThemeEvents.cartUpdate, this.#cartAddEventBound);
      this.#cartAddEventBound = null;
    }

    this.removeEventListener('input', this.#onFieldInput);
    this.removeEventListener('focusout', this.#onFieldBlur);
  }

  /**
   * Called by the product form (assets/product-form.js, `[data-validate-before-add]`) before it
   * sends the add-to-cart request. With "Recipient's email" selected, the recipient details must be
   * complete and valid: otherwise the errors are shown under each field, the first one is focused,
   * and `false` stops the add - no request is sent and the cart drawer doesn't open.
   * With "My email" selected there's nothing to check.
   * @returns {boolean} Whether the add can go ahead.
   */
  validateForSubmit() {
    if (this.#currentMode !== GiftCardRecipientForm.DeliveryMode.RECIPIENT) return true;

    this.#showAllErrors = true;

    /** @type {HTMLInputElement | HTMLTextAreaElement | null} */
    let firstInvalid = null;
    for (const field of this.#inputFields) {
      const valid = this.#validateField(field);
      if (!valid && !firstInvalid) firstInvalid = field;
    }

    if (!firstInvalid) return true;

    firstInvalid.focus();
    if (this.refs.liveRegion) {
      this.refs.liveRegion.textContent = this.dataset.errorsAnnouncement || '';
    }
    return false;
  }

  /**
   * Re-checks a field as it's edited, so an error clears (or changes) as soon as the value is fixed.
   * Before the first add attempt this only runs for a field already showing an error.
   * @param {Event} event
   */
  #onFieldInput = (event) => {
    const field = this.#fieldFromEvent(event);
    if (!field) return;
    if (this.#showAllErrors || field.getAttribute('aria-invalid') === 'true') this.#validateField(field);
  };

  /**
   * Checks a field the shopper leaves after typing in it (e.g. an incomplete email), without flagging
   * fields they haven't reached yet.
   * @param {Event} event
   */
  #onFieldBlur = (event) => {
    const field = this.#fieldFromEvent(event);
    if (!field) return;
    if (this.#showAllErrors || field.value.trim() !== '') this.#validateField(field);
  };

  /**
   * @param {Event} event
   * @returns {HTMLInputElement | HTMLTextAreaElement | null}
   */
  #fieldFromEvent(event) {
    const target = event.target;
    if (this.#currentMode !== GiftCardRecipientForm.DeliveryMode.RECIPIENT) return null;
    return this.#inputFields.find((field) => field === target) || null;
  }

  /**
   * Validates one recipient field and shows or clears its inline error.
   * @param {HTMLInputElement | HTMLTextAreaElement} field
   * @returns {boolean} Whether the field is valid.
   */
  #validateField(field) {
    const message = this.#validationMessage(field);
    this.#setFieldError(field, message);
    return message === '';
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement} field
   * @returns {string} The error to show, or '' when the field is valid.
   */
  #validationMessage(field) {
    if (field.disabled) return '';

    const { recipientEmail, recipientName, recipientSendOn } = this.refs;
    const value = field.value.trim();

    if (field.required && value === '') {
      if (field === recipientEmail) return this.dataset.errorEmailRequired || '';
      if (field === recipientName) return this.dataset.errorNameRequired || '';
      return this.dataset.errorRequired || '';
    }

    if (value === '') return '';

    // The email input's own `type="email"` and `pattern` rules (gift-card-recipient-form.liquid).
    if (field === recipientEmail && !field.checkValidity()) return this.dataset.errorEmailInvalid || '';

    // A typed date outside the allowed range (#setDateConstraints) or an incomplete one.
    if (field === recipientSendOn && !field.checkValidity()) return this.dataset.errorSendOn || '';

    return '';
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement} field
   * @param {string} message - '' clears the error.
   */
  #setFieldError(field, message) {
    const errorRef = this.#errorRefFor(field);
    const errorContainer = errorRef ? this.refs[errorRef] : null;

    if (errorContainer instanceof HTMLElement) {
      const errorTextElement = errorContainer.querySelector('span');
      if (errorTextElement) errorTextElement.textContent = message;
      errorContainer.classList.toggle('hidden', message === '');
    }

    if (message) {
      field.setAttribute('aria-invalid', 'true');
      if (errorContainer?.id) field.setAttribute('aria-describedby', errorContainer.id);
    } else {
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
    }
  }

  /**
   * @param {Element} field
   * @returns {'emailError' | 'nameError' | 'messageError' | 'sendOnError' | null}
   */
  #errorRefFor(field) {
    const { recipientEmail, recipientName, recipientMessage, recipientSendOn } = this.refs;
    if (field === recipientEmail) return 'emailError';
    if (field === recipientName) return 'nameError';
    if (field === recipientMessage) return 'messageError';
    if (field === recipientSendOn) return 'sendOnError';
    return null;
  }

  /**
   * Initialize form with default state, self delivery is selected by default
   */
  #initializeForm() {
    this.#updateButtonStates(GiftCardRecipientForm.DeliveryMode.SELF);

    this.refs.recipientFields.hidden = true;

    this.#clearRecipientFields();
    this.#disableRecipientFields();
    this.#setDateConstraints();
  }

  /**
   * Handle toggle between my email and recipient email
   * @param {string} mode - Delivery mode (either 'self' or 'recipient_form')
   * @param {Event} _event - Change event (unused)
   */
  toggleRecipientForm(mode, _event) {
    // Validate mode
    if (!Object.values(GiftCardRecipientForm.DeliveryMode).includes(mode)) {
      throw new Error(
        `Invalid delivery mode: ${mode}. Must be one of: ${Object.values(GiftCardRecipientForm.DeliveryMode).join(
          ', '
        )}`
      );
    }

    if (this.#currentMode === mode) return;
    this.#currentMode = mode;

    this.#updateFormState();
  }

  /**
   * Update form state based on current mode
   */
  #updateFormState() {
    const { DeliveryMode } = GiftCardRecipientForm;
    const isRecipientMode = this.#currentMode === DeliveryMode.RECIPIENT;

    this.#updateButtonStates(this.#currentMode);

    this.refs.recipientFields.hidden = !isRecipientMode;

    if (isRecipientMode) {
      this.#enableRecipientFields();

      this.#updateCharacterCount();

      // Announce to screen readers
      if (this.refs.liveRegion) {
        this.refs.liveRegion.textContent =
          Theme.translations?.recipient_form_fields_visible || 'Recipient form fields are now visible';
      }

      // Focus first field for accessibility
      this.refs.recipientEmail.focus();
    } else {
      this.#showAllErrors = false;
      this.#clearRecipientFields();
      this.#disableRecipientFields();

      // Announce to screen readers
      if (this.refs.liveRegion) {
        this.refs.liveRegion.textContent =
          Theme.translations?.recipient_form_fields_hidden || 'Recipient form fields are now hidden';
      }
    }

    this.dispatchEvent(
      new CustomEvent('recipient:toggle', {
        detail: {
          mode: this.#currentMode,
          recipientFormVisible: isRecipientMode,
        },
        bubbles: true,
      })
    );
  }

  /**
   * Update radio button states
   * @param {string} mode - Current delivery mode
   */
  #updateButtonStates(mode) {
    const { DeliveryMode } = GiftCardRecipientForm;

    switch (mode) {
      case DeliveryMode.SELF:
        this.refs.myEmailButton.checked = true;
        this.refs.recipientEmailButton.checked = false;
        break;

      case DeliveryMode.RECIPIENT:
        this.refs.myEmailButton.checked = false;
        this.refs.recipientEmailButton.checked = true;
        break;

      default:
        console.warn(`Unknown delivery mode: ${mode}`);
        // Default to self delivery
        this.refs.myEmailButton.checked = true;
        this.refs.recipientEmailButton.checked = false;
    }
  }

  /**
   * Clear all recipient form fields
   */
  #clearRecipientFields() {
    for (const field of this.#inputFields) {
      field.value = '';
    }

    this.#updateCharacterCount();
    this.#clearErrorMessages();
  }

  /**
   * Disable recipient form fields when sending to self
   */
  #disableRecipientFields() {
    for (const field of this.#inputFields) {
      field.disabled = true;
      field.removeAttribute('required');
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
    }

    // Remove control field when sending to self
    const controlFlag = this.querySelector('input[name="properties[__shopify_send_gift_card_to_recipient]"]');
    if (controlFlag) {
      controlFlag.remove();
    }

    if (this.refs.timezoneOffset) {
      this.refs.timezoneOffset.disabled = true;
      this.refs.timezoneOffset.value = '';
    }

    this.#clearErrorMessages();
  }

  /**
   * Enable recipient form fields when sending to recipient
   */
  #enableRecipientFields() {
    for (const field of this.#inputFields) {
      field.disabled = false;
      // Required fields are marked in the markup (gift-card-recipient-form.liquid).
      field.required = field.dataset.required === 'true';
    }

    // Add control field when sending to recipient
    let controlFlag = this.querySelector('input[name="properties[__shopify_send_gift_card_to_recipient]"]');
    if (!controlFlag) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[__shopify_send_gift_card_to_recipient]';
      input.value = 'on';
      this.appendChild(input);
    }

    // Enable and set timezone offset
    if (this.refs.timezoneOffset) {
      this.refs.timezoneOffset.disabled = false;
      this.refs.timezoneOffset.value = new Date().getTimezoneOffset().toString();
    }

    // Set date constraints when enabling fields
    this.#setDateConstraints();
  }

  /**
   * Update character count display
   */
  #updateCharacterCount() {
    if (!this.refs.characterCount) return;

    const currentLength = this.refs.recipientMessage.value.length;
    const maxLength = this.refs.recipientMessage.maxLength;

    const template = this.refs.characterCount.getAttribute('data-template');
    if (!template) return;

    const updatedText = template.replace('[current]', currentLength.toString()).replace('[max]', maxLength.toString());

    this.refs.characterCount.textContent = updatedText;
  }

  /**
   * Set date constraints for the send on date picker
   * Prevents selecting past dates and limits to 90 days in the future
   */
  #setDateConstraints() {
    const today = new Date();
    const maxDate = new Date();
    maxDate.setDate(today.getDate() + 90);

    // Format dates as YYYY-MM-DD
    /**
     * @param {Date} date
     * @returns {string}
     */
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    this.refs.recipientSendOn.setAttribute('min', formatDate(today));
    this.refs.recipientSendOn.setAttribute('max', formatDate(maxDate));
  }

  /**
   * Handles cart error events
   * @param {CartErrorEvent} event - The cart error event
   */
  #displayCartError(event) {
    // Only errors from the add this form is part of.
    if (!(event.target instanceof Node) || !event.target.contains(this)) return;

    if (event.detail?.data) {
      const { message, errors, description } = event.detail.data;

      // Display the error message
      if (errors && typeof errors === 'object') {
        this.#displayErrorMessage(message || 'There was an error', errors);
      } else if (message) {
        this.#displayErrorMessage(message, description);
      }
    }
  }

  /**
   * Display error messages in the appropriate error containers
   * @param {string} title - The main error message title
   * @param {Object} body - Error details
   */
  #displayErrorMessage(title, body) {
    this.#clearErrorMessages();

    if (typeof body === 'object' && body !== null) {
      /** @type {Record<string, {inputRef: string, errorRef: string}>} */
      const fieldMap = {
        email: { inputRef: 'recipientEmail', errorRef: 'emailError' },
        name: { inputRef: 'recipientName', errorRef: 'nameError' },
        message: { inputRef: 'recipientMessage', errorRef: 'messageError' },
        send_on: { inputRef: 'recipientSendOn', errorRef: 'sendOnError' },
      };

      for (const [field, errorMessages] of Object.entries(body)) {
        const fieldConfig = fieldMap[field];
        if (!fieldConfig) continue;

        const { inputRef, errorRef } = fieldConfig;
        const errorContainer = this.refs[errorRef];
        const inputElement = this.refs[inputRef];

        if (errorContainer && errorContainer instanceof HTMLElement) {
          const errorTextElement = errorContainer.querySelector('span');
          if (errorTextElement) {
            const message = Array.isArray(errorMessages) ? errorMessages.join(', ') : errorMessages;
            errorTextElement.textContent = `${message}.`;
          }

          errorContainer.classList.remove('hidden');
        }

        if (inputElement && inputElement instanceof HTMLElement) {
          // Set ARIA attributes for accessibility
          inputElement.setAttribute('aria-invalid', 'true');
          if (errorContainer instanceof HTMLElement && errorContainer.id) {
            inputElement.setAttribute('aria-describedby', errorContainer.id);
          }
        }
      }
    }

    // Announce errors to screen readers
    if (this.refs.liveRegion) {
      this.refs.liveRegion.textContent =
        title || Theme.translations?.recipient_form_error || 'There was an error with the form submission';
    }
  }

  /**
   * Clear all error messages and reset ARIA attributes
   */
  #clearErrorMessages() {
    // List of error container refs
    const errorRefs = ['emailError', 'nameError', 'messageError', 'sendOnError'];

    for (const errorRef of errorRefs) {
      const errorContainer = this.refs[errorRef];
      if (errorContainer && errorContainer instanceof HTMLElement) {
        errorContainer.classList.add('hidden');
        const errorTextElement = errorContainer.querySelector('span');
        if (errorTextElement) {
          errorTextElement.textContent = '';
        }
      }
    }

    // Remove ARIA attributes from all input fields
    for (const field of this.#inputFields) {
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
    }

    // Clear live region announcement
    if (this.refs.liveRegion) {
      this.refs.liveRegion.textContent = '';
    }
  }

  /**
   * Clears the errors once this form's product has actually been added. A failed add also sends a
   * cart update event (product-form.js), which used to wipe the error it had just displayed; cart
   * updates from elsewhere (the cart drawer, other products) leave this form alone.
   * @param {Event} event
   */
  #handleCartAdd(event) {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (detail?.data?.didError) return;
    if (!(event.target instanceof Node) || !event.target.contains(this)) return;

    this.#showAllErrors = false;
    this.#clearErrorMessages();
  }
}

// Register the custom element
customElements.define('gift-card-recipient-form', GiftCardRecipientForm);
